import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {
  Loader2, ArrowLeft, FolderLock, FolderOpen, FileText, ChevronRight,
  X, ChevronLeft, ZoomIn, ZoomOut, Info, CheckCircle2, Check,
} from "lucide-react";
import { toast } from "sonner";
import { fmtLog, fmtLogFull } from "@/lib/tz";

const CATS = {
  internal: {
    title: "Internal Documents",
    subtitle: "Care plans & signed documents prepared for support workers.",
    Icon: FolderLock, tint: "text-[#00c86e]", bg: "bg-emerald-500/15",
  },
  external: {
    title: "External Documents",
    subtitle: "Documents from other systems shared with you (e.g. PBSP).",
    Icon: FolderOpen, tint: "text-sky-400", bg: "bg-sky-500/15",
  },
};

const fmt = (d) => {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const dt = new Date(`${s}T00:00:00`);
  return isNaN(dt) ? s : dt.toLocaleDateString();
};

// Full-screen, view-only, pinch-zoomable document viewer.
function DocViewer({ name, kind, doc, onClose }) {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [urls, setUrls] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [acked, setAcked] = useState(false);
  const [ackTs, setAckTs] = useState(null);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    let live = true;
    const created = [];
    (async () => {
      try {
        const m = await api.workerDocPages(name, kind, doc.id);
        if (!live) return;
        setMeta(m); setAcked(!!m.acknowledged); setAckTs(m.acknowledged_at || null);
        if (!m.pages) { setErr("Preview isn't available for this document."); setLoading(false); return; }
        const arr = [];
        for (let i = 0; i < m.pages; i++) {
          const blob = await api.workerDocPage(name, kind, doc.id, i);
          const u = URL.createObjectURL(blob);
          created.push(u); arr.push(u);
          if (live) setUrls([...arr]);
        }
      } catch (e) {
        if (live) setErr(e.response?.status === 415 ? "Preview isn't available for this document." : "Could not open document.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; created.forEach((u) => URL.revokeObjectURL(u)); };
  }, [name, kind, doc.id]);

  const ack = async () => {
    setAcking(true);
    try {
      const r = await api.workerAckDocument(name, kind, doc.id);
      setAcked(true); setAckTs(r.ts);
      toast.success("Marked as read.");
    } catch { toast.error("Could not record acknowledgement."); }
    finally { setAcking(false); }
  };

  const block = useCallback((e) => e.preventDefault(), []);
  const wm = `${user?.name || "Support Worker"} · ${fmtLogFull(new Date().toISOString())}`;

  return (
    <div className="fixed inset-0 z-50 bg-[#0b0f10] flex flex-col select-none"
      onContextMenu={block} data-testid="worker-doc-viewer"
      style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}>
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10 bg-black/40">
        <button onClick={onClose} className="p-1.5 -ml-1 text-slate-300 active:text-white" data-testid="worker-doc-viewer-close">
          <X className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-semibold truncate">{doc.title}</div>
          {meta?.pages > 1 && <div className="text-[10px] text-slate-400">Page {idx + 1} of {meta.pages}</div>}
        </div>
      </div>

      {/* read-receipt bar (non-blocking) */}
      {!err && !loading && (
        acked ? (
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border-b border-emerald-500/20 text-emerald-300 text-xs" data-testid="worker-doc-acked">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            You marked this as read{ackTs ? ` · ${fmtLog(ackTs)}` : ""}
          </div>
        ) : (
          <button onClick={ack} disabled={acking}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00c86e] text-black font-semibold text-sm active:bg-[#00b563] disabled:opacity-60"
            data-testid="worker-doc-ack-btn">
            {acking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            I've read this document
          </button>
        )
      )}

      {/* body */}
      <div className="flex-1 relative overflow-hidden">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" />
          </div>
        )}
        {err && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-8 text-center">
            <Info className="h-6 w-6 text-slate-500" />
            <div className="text-sm text-slate-400">{err}</div>
          </div>
        )}
        {!err && urls[idx] && (
          <TransformWrapper key={idx} minScale={1} maxScale={6} doubleClick={{ mode: "toggle", step: 2 }} centerOnInit>
            {({ zoomIn, zoomOut }) => (
              <>
                <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "100%" }}>
                  <div className="relative w-full">
                    <img src={urls[idx]} alt={`Page ${idx + 1}`} draggable={false}
                      onDragStart={block} onContextMenu={block}
                      className="w-full h-auto pointer-events-none" data-testid="worker-doc-page-img" />
                    {/* diagonal watermark deterrent */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="text-white/10 text-xs font-semibold -rotate-45 whitespace-nowrap">{wm}</span>
                    </div>
                  </div>
                </TransformComponent>
                {/* zoom controls */}
                <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                  <button onClick={() => zoomIn()} className="h-10 w-10 rounded-full bg-black/60 border border-white/15 flex items-center justify-center text-white active:bg-black/80" data-testid="worker-doc-zoom-in">
                    <ZoomIn className="h-4 w-4" />
                  </button>
                  <button onClick={() => zoomOut()} className="h-10 w-10 rounded-full bg-black/60 border border-white/15 flex items-center justify-center text-white active:bg-black/80" data-testid="worker-doc-zoom-out">
                    <ZoomOut className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </TransformWrapper>
        )}
      </div>

      {/* page nav */}
      {meta?.pages > 1 && !err && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/10 bg-black/40">
          <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}
            className="flex items-center gap-1 text-xs text-slate-300 disabled:opacity-30" data-testid="worker-doc-prev">
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <span className="text-[11px] text-slate-500">{idx + 1} / {meta.pages}</span>
          <button onClick={() => setIdx((i) => Math.min(meta.pages - 1, i + 1))} disabled={idx >= meta.pages - 1}
            className="flex items-center gap-1 text-xs text-slate-300 disabled:opacity-30" data-testid="worker-doc-next">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function WorkerParticipantDocuments() {
  const { name, category } = useParams();
  const nav = useNavigate();
  const cat = CATS[category] || CATS.internal;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.workerParticipantDocuments(name)
      .then((d) => setItems((d && d[category]) || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load documents."))
      .finally(() => setLoading(false));
  }, [name, category]);

  const { Icon } = cat;

  return (
    <div className="px-4 pt-4 pb-6" data-testid="worker-participant-documents">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-slate-400 text-xs mb-3" data-testid="worker-docs-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-center gap-3 mb-1">
        <div className={`h-11 w-11 rounded-xl ${cat.bg} flex items-center justify-center shrink-0`}>
          <Icon className={`h-5 w-5 ${cat.tint}`} />
        </div>
        <div>
          <h1 className="text-lg font-bold text-white tracking-tight">{cat.title}</h1>
          <p className="text-[11px] text-slate-400">{cat.subtitle}</p>
        </div>
      </div>

      <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-[10px] text-slate-400">
        <Info className="h-3 w-3 mt-0.5 shrink-0" /> These documents are view-only inside the app. Pinch or double-tap to zoom.
      </div>

      {loading ? (
        <div className="flex justify-center pt-16"><Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" /></div>
      ) : items.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-xs text-slate-500" data-testid="worker-docs-empty">
          No {cat.title.toLowerCase()} shared with you for this participant.
        </div>
      ) : (
        <div className="mt-4 space-y-2.5">
          {items.map((d) => (
            <button key={d.id} onClick={() => setOpen(d)}
              className="w-full text-left rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-3 active:bg-white/10"
              data-testid={`worker-doc-item-${d.id}`}>
              <FileText className={`h-5 w-5 ${cat.tint} shrink-0`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-medium truncate">{d.title}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {category === "external" && d.source_system ? `${d.source_system} · ` : ""}
                  {category === "external" ? `Active from ${fmt(d.date)}` : (d.date ? `Signed ${fmt(d.date)}` : "")}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {open && <DocViewer name={name} kind={category} doc={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
